import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { api } from '@/lib/api/client';
import { useAuthStore } from '@/lib/stores/auth';
import { useAdminStore } from '@/lib/stores/admin';
import type { AdminUser } from '@/lib/stores/admin/types';
import { AdminLayout } from '@/components/layouts/admin-layout';
import FilterBar from '@/components/common/filter-bar';
import { USER_FILTERS } from '@/lib/shared-types/filters';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Search, Pencil, Trash2, UserPlus, Users, X, Plus, ChevronRight } from 'lucide-react';
import { PageHeader } from '@/components/page/page-header';
import { PageToolbar } from '@/components/page/page-toolbar';
import { PageSection } from '@/components/page/page-section';
import { DataGridTable, type DataGridColumn } from '@/components/ui/data-grid-table';

interface SegmentSummary {
  id: number;
  name: string;
  memberCount: number;
  updatedAt: string;
}

interface SegmentMember {
  userId: number;
  name: string;
  email: string;
}

interface UserSearchResult {
  id: number;
  name: string;
  email: string;
}

export default function AdminUsersPage() {
  const navigate = useNavigate();
  const { users, pagination, fetchUsers, isLoading } = useAdminStore();
  const { isInitialized } = useAuthStore();

  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const pageSize = 20;

  const [filters, setFilters] = useState<Record<string, string | string[]>>({});
  const [addUserOpen, setAddUserOpen] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [newName, setNewName] = useState('');
  const [newRole, setNewRole] = useState('member');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [segments, setSegments] = useState<SegmentSummary[]>([]);
  const [segmentsLoading, setSegmentsLoading] = useState(false);
  const [addSegmentOpen, setAddSegmentOpen] = useState(false);
  const [newSegmentName, setNewSegmentName] = useState('');
  const [editingSegment, setEditingSegment] = useState<SegmentSummary | null>(null);
  const [editingSegmentName, setEditingSegmentName] = useState('');
  const [membersDialogOpen, setMembersDialogOpen] = useState(false);
  const [segmentMembers, setSegmentMembers] = useState<SegmentMember[]>([]);
  const [memberSearch, setMemberSearch] = useState('');
  const [memberSearchResults, setMemberSearchResults] = useState<UserSearchResult[]>([]);

  useEffect(() => {
    if (isInitialized) {
      setPage(1);
      setHasMore(false);
    }
  }, [isInitialized, filters]);

  useEffect(() => {
    if (isInitialized) {
      const searchQuery = (filters.search as string) || '';
      const userRole = (filters.role as string) || '';
      const userStatus = (filters.status as string) || '';

      fetchUsers({
        searchQuery,
        userRole: userRole !== 'all' && userRole !== '' ? userRole : undefined,
        userStatus: userStatus !== 'all' && userStatus !== '' ? userStatus : undefined,
      }, page, pageSize);
    }
  }, [fetchUsers, isInitialized, page, filters]);

  useEffect(() => {
    if (pagination?.users) {
      const totalPages = Math.ceil(pagination.users.total / pagination.users.pageSize);
      setHasMore(page < totalPages);
    }
  }, [pagination, page]);

  const handleAddUser = async () => {
    if (!newEmail || !newName) return;
    setIsSubmitting(true);
    try {
      const createResponse = await api.post('/api/admin/users', { email: newEmail, name: newName, roleName: newRole });
      if (!createResponse.success) {
        toast.error(createResponse.error?.message || 'Failed to create user');
        return;
      }
      toast.success('User created successfully');
      setAddUserOpen(false);
      setNewEmail('');
      setNewName('');
      setNewRole('member');
      fetchUsers({}, 1, pageSize);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to create user';
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const fetchSegments = async () => {
    setSegmentsLoading(true);
    try {
      const response = await api.get<{ segments: SegmentSummary[] }>('/api/admin/segments');
      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to fetch segments');
      }
      setSegments(response.data?.segments || []);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to fetch segments';
      toast.error(message);
      setSegments([]);
    } finally {
      setSegmentsLoading(false);
    }
  };

  const handleCreateSegment = async () => {
    const name = newSegmentName.trim();
    if (!name) return;
    try {
      const response = await api.post('/api/admin/segments', { name });
      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to create segment');
      }
      toast.success('Segment created');
      setNewSegmentName('');
      setAddSegmentOpen(false);
      await fetchSegments();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to create segment';
      toast.error(message);
    }
  };

  const handleRenameSegment = async () => {
    if (!editingSegment) return;
    const name = editingSegmentName.trim();
    if (!name) return;
    try {
      const response = await api.put(`/api/admin/segments/${editingSegment.id}`, { name });
      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to rename segment');
      }
      toast.success('Segment updated');
      setEditingSegment(null);
      setEditingSegmentName('');
      await fetchSegments();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to rename segment';
      toast.error(message);
    }
  };

  const handleDeleteSegment = async (segmentId: number) => {
    try {
      const response = await api.delete(`/api/admin/segments/${segmentId}`);
      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to delete segment');
      }
      toast.success('Segment deleted');
      await fetchSegments();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to delete segment';
      toast.error(message);
    }
  };

  const openMembersDialog = async (segment: SegmentSummary) => {
    setEditingSegment(segment);
    setMembersDialogOpen(true);
    setMemberSearch('');
    setMemberSearchResults([]);
    try {
      const response = await api.get<{ segment: { members: SegmentMember[] } }>(`/api/admin/segments/${segment.id}`);
      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to load segment members');
      }
      setSegmentMembers(response.data?.segment?.members || []);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load segment members';
      toast.error(message);
      setSegmentMembers([]);
    }
  };

  const searchUsers = async (query: string) => {
    if (query.trim().length < 2) {
      setMemberSearchResults([]);
      return;
    }
    try {
      const response = await api.get<{ users: UserSearchResult[] }>(`/api/admin/users/search?q=${encodeURIComponent(query)}`);
      if (response.success) {
        setMemberSearchResults(response.data?.users || []);
      }
    } catch {
      setMemberSearchResults([]);
    }
  };

  const addMemberToSegment = (user: UserSearchResult) => {
    setSegmentMembers((prev) => {
      if (prev.some((member) => member.userId === user.id)) return prev;
      return [...prev, { userId: user.id, name: user.name, email: user.email }];
    });
  };

  const removeMemberFromSegment = (userId: number) => {
    setSegmentMembers((prev) => prev.filter((member) => member.userId !== userId));
  };

  const saveSegmentMembers = async () => {
    if (!editingSegment) return;
    try {
      const response = await api.put(`/api/admin/segments/${editingSegment.id}/members`, {
        userIds: segmentMembers.map((member) => member.userId),
      });
      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to save segment members');
      }
      toast.success('Segment members saved');
      setMembersDialogOpen(false);
      await fetchSegments();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to save segment members';
      toast.error(message);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (membersDialogOpen) {
        searchUsers(memberSearch);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [memberSearch, membersDialogOpen]);

  useEffect(() => {
    if (isInitialized) {
      fetchSegments();
    }
  }, [isInitialized]);

  const getRoleBadge = (role: string) => {
    const variants: Record<string, 'default' | 'secondary' | 'outline'> = {
      admin: 'default',
      media_team: 'secondary',
      moderator: 'outline',
      pastor: 'outline',
      member: 'outline',
    };
    return <Badge className="capitalize" variant={variants[role] || 'outline'}>{role.replace('_', ' ')}</Badge>;
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, 'default' | 'secondary' | 'destructive'> = {
      active: 'default',
      pending: 'secondary',
      banned: 'destructive',
    };
    return <Badge className="capitalize" variant={variants[status] || 'outline'}>{status}</Badge>;
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid Date';
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const columns = useMemo<DataGridColumn<AdminUser>[]>(() => [
    {
      id: 'user',
      header: 'User',
      mobileLabel: 'User',
      cell: (user) => (
        <div className="flex items-center gap-3">
          <Avatar className="h-9 w-9 border-2 border-primary/20">
            <AvatarFallback className="bg-primary/10 text-primary-foreground">{user.name.charAt(0).toUpperCase()}</AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <button
              type="button"
              className="truncate text-left font-medium text-foreground hover:underline"
              onClick={() => navigate(`/admin/users/${user.id}`)}
            >
              {user.name}
            </button>
            <p className="truncate text-xs text-muted-foreground">{user.email}</p>
          </div>
        </div>
      ),
    },
    {
      id: 'role',
      header: 'Role',
      mobileLabel: 'Role',
      cell: (user) => getRoleBadge(user.role),
    },
    {
      id: 'status',
      header: 'Status',
      mobileLabel: 'Status',
      cell: (user) => getStatusBadge(user.status),
    },
    {
      id: 'activity',
      header: 'Activity',
      mobileLabel: 'Activity',
      cell: (user) => (
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <Badge variant="secondary" className="px-1.5 py-0 h-5 font-mono text-[10px]">{user.loginCount}</Badge>
            <span className="text-xs text-muted-foreground">logins</span>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="secondary" className="px-1.5 py-0 h-5 font-mono text-[10px]">{user.commentsPosted}</Badge>
            <span className="text-xs text-muted-foreground">comments</span>
          </div>
        </div>
      ),
    },
    {
      id: 'dates',
      header: 'Dates',
      mobileLabel: 'Dates',
      cell: (user) => (
        <div className="text-xs space-y-1">
          <div className="flex items-center justify-between gap-4">
            <span className="text-muted-foreground">Joined:</span>
            <span className="font-medium">{formatDate(user.createdAt)}</span>
          </div>
          <div className="flex items-center justify-between gap-4">
            <span className="text-muted-foreground">Active:</span>
            <span className="font-medium">{formatDate(user.lastActivityAt)}</span>
          </div>
        </div>
      ),
    },
    {
      id: 'actions',
      header: '',
      mobileLabel: 'Actions',
      className: 'text-right',
      cell: (user) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(`/admin/users/${user.id}`)}
          className="hover:bg-primary/5 hover:text-primary transition-colors"
        >
          Manage
          <ChevronRight className="ml-1 h-3.5 w-3.5" />
        </Button>
      ),
    },
  ], [navigate]);

  if (isLoading && users.length === 0) {
    return (
      <AdminLayout>
        <div className="flex min-h-[400px] items-center justify-center">
          <LoadingSpinner size="lg" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <PageHeader
        title="Users"
        actions={(
          <Dialog open={addUserOpen} onOpenChange={setAddUserOpen}>
            <DialogTrigger asChild>
              <Button>
                <UserPlus className="mr-2 h-4 w-4" />
                Add User
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add New User</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-2">
                <div className="space-y-1">
                  <Label htmlFor="new-user-email">Email</Label>
                  <Input
                    id="new-user-email"
                    type="email"
                    placeholder="user@example.com"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="new-user-name">Name</Label>
                  <Input
                    id="new-user-name"
                    placeholder="Full name"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                  />
                </div>
                <div className="space-y-1">
                  <Label>Role</Label>
                  <Select value={newRole} onValueChange={setNewRole}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="member">Member</SelectItem>
                      <SelectItem value="moderator">Moderator</SelectItem>
                      <SelectItem value="pastor">Pastor</SelectItem>
                      <SelectItem value="media_team">Media Team</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
          <DialogFooter className="mt-4 flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2">
            <Button variant="outline" onClick={() => setAddUserOpen(false)} className="w-full sm:w-auto mt-2 sm:mt-0">Cancel</Button>
            <Button onClick={handleAddUser} disabled={isSubmitting || !newEmail || !newName} className="w-full sm:w-auto">
              {isSubmitting ? 'Creating...' : 'Create User'}
            </Button>
          </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      />

      <div className="@container/page-toolbar">
        <PageToolbar
          leading={(
            <FilterBar
              filters={USER_FILTERS}
              activeFilters={filters}
              onFilterChange={setFilters}
            />
          )}
        />
      </div>

      <PageSection
        title={`Directory (${users.length})`}
        description="Scrollable table view across all screen sizes."
        className="border-border/70"
      >
        <div className="@container/data-grid">
          <DataGridTable
            rows={users}
            columns={columns}
            getRowKey={(user) => user.id}
            isLoading={isLoading}
            hasMore={hasMore}
            onLoadMore={() => setPage((p) => p + 1)}
            emptyMessage="No users found"
          />
        </div>
      </PageSection>

      <PageSection
        title={`Segments (${segments.length})`}
        description="Organize users into reusable lists for invite-only stream access."
        className="border-border/70"
        actions={
          <Dialog open={addSegmentOpen} onOpenChange={setAddSegmentOpen}>
            <DialogTrigger asChild>
              <Button size="sm">
                <Plus className="mr-2 h-4 w-4" />
                Add Segment
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create New Segment</DialogTitle>
              </DialogHeader>
              <div className="space-y-2">
                <Label htmlFor="segment-name">Segment Name</Label>
                <Input
                  id="segment-name"
                  placeholder="Enter segment name..."
                  value={newSegmentName}
                  onChange={(e) => setNewSegmentName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && newSegmentName.trim()) {
                      handleCreateSegment();
                    }
                  }}
                />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setAddSegmentOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleCreateSegment} disabled={!newSegmentName.trim()}>
                  Create Segment
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      >
        {segmentsLoading ? (
          <div className="flex min-h-[120px] items-center justify-center">
            <LoadingSpinner size="sm" />
          </div>
        ) : segments.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 rounded-xl border border-dashed bg-muted/30">
            <Users className="h-10 w-10 text-muted-foreground opacity-20 mb-3" />
            <p className="text-sm text-muted-foreground font-medium">No segments yet</p>
            <p className="text-xs text-muted-foreground/70">Create one above to get started</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {segments.map((segment) => (
              <Card key={segment.id} className="group flex flex-col justify-between hover:border-primary/50 transition-colors">
                <CardHeader className="pb-4">
                  <CardTitle className="flex items-start justify-between gap-2 text-base font-semibold">
                    <span className="truncate flex-1">{segment.name}</span>
                    <Badge variant="secondary" className="shrink-0">{segment.memberCount} members</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="flex flex-col flex-grow justify-end">
                  <div className="flex items-center gap-2 text-xs text-muted-foreground mb-4">
                    <Users className="h-3.5 w-3.5 text-primary/70" />
                    Last updated: {formatDate(segment.updatedAt)}
                  </div>
                  <div className="flex flex-wrap gap-2 justify-end">
                    <Button size="sm" variant="outline" onClick={() => openMembersDialog(segment)} className="w-full sm:w-auto">
                      Manage Members
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setEditingSegment(segment);
                        setEditingSegmentName(segment.name);
                      }}
                      className="group-hover:text-primary transition-colors w-full sm:w-auto"
                    >
                      <Pencil className="mr-1 h-3.5 w-3.5" />
                      Rename
                    </Button>
                    <Button size="sm" variant="destructive" onClick={() => handleDeleteSegment(segment.id)} className="w-full sm:w-auto">
                      <Trash2 className="mr-1 h-3.5 w-3.5" />
                      Delete
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </PageSection>

      <Dialog
        open={!!editingSegment && !membersDialogOpen}
        onOpenChange={(open) => {
          if (!open && !membersDialogOpen) {
            setEditingSegment(null);
            setEditingSegmentName('');
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rename Segment</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="segment-name">Name</Label>
            <Input
              id="segment-name"
              value={editingSegmentName}
              onChange={(e) => setEditingSegmentName(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setEditingSegment(null);
                setEditingSegmentName('');
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleRenameSegment} disabled={!editingSegmentName.trim()}>
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={membersDialogOpen}
        onOpenChange={(open) => {
          setMembersDialogOpen(open);
          if (!open) {
            setEditingSegment(null);
            setMemberSearch('');
            setMemberSearchResults([]);
          }
        }}
      >
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Manage Segment Members</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="member-search">Find users</Label>
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  id="member-search"
                  placeholder="Search by name or email"
                  value={memberSearch}
                  onChange={(e) => setMemberSearch(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>

            {memberSearchResults.length > 0 && (
              <div className="rounded-md border bg-muted/50">
                <ScrollArea className="h-[120px]">
                  <div className="p-2 space-y-1">
                    {memberSearchResults.map((user) => (
                      <button
                        key={user.id}
                        type="button"
                        className="flex w-full items-center justify-between rounded-sm px-2 py-1.5 text-left text-sm hover:bg-background transition-colors"
                        onClick={() => addMemberToSegment(user)}
                      >
                        <div className="min-w-0">
                          <p className="font-medium truncate">{user.name}</p>
                          <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                        </div>
                        <Plus className="h-4 w-4 text-primary shrink-0 ml-2" />
                      </button>
                    ))}
                  </div>
                </ScrollArea>
              </div>
            )}

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-semibold">Selected members</Label>
                <Badge variant="secondary">{segmentMembers.length}</Badge>
              </div>
              <Separator />
              <div className="rounded-md border">
                <ScrollArea className="h-[200px]">
                  {segmentMembers.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-[200px] text-muted-foreground space-y-2">
                      <Users className="h-8 w-8 opacity-20" />
                      <p className="text-sm">No members selected</p>
                    </div>
                  ) : (
                    <div className="p-2 space-y-1">
                      {segmentMembers.map((member) => (
                        <div key={member.userId} className="flex items-center justify-between rounded-sm px-2 py-1.5 text-sm hover:bg-muted transition-colors group">
                          <div className="min-w-0">
                            <p className="font-medium truncate">{member.name}</p>
                            <p className="text-xs text-muted-foreground truncate">{member.email}</p>
                          </div>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-8 w-8 text-muted-foreground hover:text-destructive transition-colors"
                            onClick={() => removeMemberFromSegment(member.userId)}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </div>
            </div>
          </div>
          <DialogFooter className="mt-4 flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2">
            <Button variant="outline" onClick={() => setMembersDialogOpen(false)} className="w-full sm:w-auto mt-2 sm:mt-0">Cancel</Button>
            <Button onClick={saveSegmentMembers} className="w-full sm:w-auto">Save Members</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
